import React from 'react';
import { Collapse, Tag, Typography, Space, Tooltip, Card } from 'antd';
import { FileTextOutlined, LinkOutlined, DatabaseOutlined } from '@ant-design/icons';

const { Panel } = Collapse;
const { Text, Paragraph } = Typography;

interface Source {
  id: string;
  name: string;
  content: string;
  score: number;
  segment_id?: string;
  url?: string;
}

interface SourceReferencesProps {
  sources: Source[];
  response_type?: 'knowledge_based' | 'generated';
}

const SourceReferences: React.FC<SourceReferencesProps> = ({ sources, response_type }) => {
  // 如果没有引用来源，不显示任何内容
  if (!sources || sources.length === 0) {
    return null;
  }

  // 获取相关度分数的颜色
  const getScoreColor = (score: number) => {
    if (score >= 0.8) return 'green';
    if (score >= 0.6) return 'orange';
    return 'red';
  };

  // 格式化相关度分数
  const formatScore = (score: number) => {
    return `${(score * 100).toFixed(0)}%`;
  };

  return (
    <div style={{ marginTop: 8 }}>
      <Collapse 
        size="small"
        ghost
        bordered={false}
        style={{ backgroundColor: '#f8f9fa' }}
      >
        <Panel
          header={
            <Space>
              <FileTextOutlined style={{ color: '#52c41a' }} />
              <Text strong style={{ color: '#52c41a', fontSize: '13px' }}>
                📖 信息来源 ({sources.length} 个知识库引用)
              </Text>
              <Tag color="green" style={{ fontSize: '11px', padding: '1px 6px' }}>已验证</Tag>
            </Space>
          }
          key="sources"
          style={{ 
            backgroundColor: '#f6ffed',
            border: '1px solid #b7eb8f',
            borderRadius: '4px'
          }}
        >
          <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
            {sources.map((source, index) => (
              <Card 
                key={source.id}
                size="small"
                style={{ 
                  marginBottom: index < sources.length - 1 ? 8 : 0,
                  backgroundColor: '#ffffff',
                  border: '1px solid #e8e8e8'
                }}
                title={
                  <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                    <Space>
                      <FileTextOutlined style={{ color: '#1890ff' }} />
                      <Text strong style={{ fontSize: '13px' }}>
                        {source.name}
                      </Text>
                    </Space>
                    <Space>
                      <Tooltip title="相关度分数">
                        <Tag color={getScoreColor(source.score)} style={{ fontSize: '11px', padding: '1px 6px' }}>
                          相关度: {formatScore(source.score)}
                        </Tag>
                      </Tooltip>
                      {source.url && (
                        <Tooltip title="查看原文档">
                          <LinkOutlined 
                            style={{ color: '#1890ff', cursor: 'pointer' }}
                            onClick={() => window.open(source.url, '_blank')}
                          />
                        </Tooltip>
                      )}
                    </Space>
                  </Space>
                }
              >
                <Paragraph
                  style={{ 
                    margin: 0,
                    fontSize: '12px',
                    lineHeight: '1.5',
                    color: '#666'
                  }}
                  ellipsis={{ 
                    rows: 3, 
                    expandable: true, 
                    symbol: '展开更多' 
                  }}
                >
                  {source.content}
                </Paragraph>
                {source.segment_id && (
                  <Text 
                    type="secondary" 
                    style={{ fontSize: '11px', marginTop: 8, display: 'block' }}
                  >
                    段落ID: {source.segment_id}
                  </Text>
                )}
              </Card>
            ))}
          </div>
        </Panel>
      </Collapse>
    </div>
  );
};

export default SourceReferences;